import {
    removePendingMessage,
    sendMessage,
} from './sendMessage'
import {ActionReducerMapBuilder} from '@reduxjs/toolkit'
import {NinchatState} from 'api/types'
import {acceptAudience} from './acceptAudience'
import {accessCreated} from '../event/accessCreated'
import {accessFound} from '../event/accessFound'
import {ack} from 'api/event/ack'
import {addMember} from './addMember'
import {assignTranscript} from './assignTranscript'
import {audienceEnqueued} from '../event/audienceEnqueued'
import {audienceRangeCommentContents} from '../event/audienceRangeCommentContents'
import {audienceRegistered} from '../event/audienceRegistered'
import {beginSso} from './beginSso'
import {channelCreated} from 'api/event/channelCreated'
import {channelFound} from '../event/channelFound'
import {channelJoined} from '../event/channelJoined'
import {channelParted} from '../event/channelParted'
import {channelUpdated} from '../event/channelUpdated'
import {closeSession} from './closeSession'
import {commentCreated} from '../event/commentCreated'
import {createAccess} from './createAccess'
import {createChannel} from './createChannel'
import {createComment} from './createComment'
import {createIdentity} from './createIdentity'
import {createIdentityWithAuthResetAccess} from './createIdentityWithAuthResetAccess'
import {createMasterKey} from './createMasterKey'
import {createQueue} from './createQueue'
import {createSession} from './createSession'
import {createTag} from './createTag'
import {deleteIdentity} from './deleteIdentity'
import {deleteMasterKey} from './deleteMasterKey'
import {deleteMessage} from './deleteMessage'
import {deleteTag} from './deleteTag'
import {describeAccess} from './describeAccess'
import {describeChannel} from './describeChannel'
import {describeFile} from './describeFile'
import {describeIdentity} from './describeIdentity'
import {describeQueue} from './describeQueue'
import {describeQueueTranscripts} from './describeQueueTranscripts'
import {describeRealm} from './describeRealm'
import {describeRealmQueues} from './describeRealmQueues'
import {describeTag} from './describeTag'
import {describeTags} from './describeTags'
import {describeTranscript} from './describeTranscript'
import {describeUser} from './describeUser'
import {dialogueUpdated} from '../event/dialogueUpdated'
import {discoverJitsi} from './discoverJitsi'
import {endAudience} from './endAudience'
import {fileFound} from '../event/fileFound'
import {getAudienceRangeComments} from './getAudienceRangeComments'
import {getQueueActivity} from './getQueueActivity'
import {getQueueStats} from './getQueueStats'
import {getTranscript} from './getTranscript'
import {identityCreated} from '../event/identityCreated'
import {identityDeleted} from '../event/identityDeleted'
import {identityFound} from '../event/identityFound'
import {identityUpdated} from '../event/identityUpdated'
import {jitsiDiscovered} from '../event/jitsiDiscovered'
import {joinChannel} from './joinChannel'
import {joinPermissionGroup} from './joinPermissionGroup'
import {joinPermissionGroupAck} from './joinPermissionGroupAck'
import {loadHistory} from './loadHistory'
import {masterKeyCreated} from 'api/event/masterKeyCreated'
import {masterKeyDeleted} from 'api/event/masterKeyDeleted'
import {messageUpdated} from '../event/messageUpdated'
import {partChannel} from './partChannel'
import {queueActivity} from '../event/queueActivity'
import {queueCreated} from 'api/event/queueCreated'
import {queueFound} from '../event/queueFound'
import {queueStatsContents} from '../event/queueStatsContents'
import {queueTranscriptsFound} from '../event/queueTranscriptsFound'
import {queueUpdated} from '../event/queueUpdated'
import {realmFound} from '../event/realmFound'
import {realmQueuesFound} from '../event/realmQueuesFound'
import {realmUpdated} from 'api/event/realmUpdated'
import {registerAudience} from './registerAudience'
import {removeMember} from './removeMember'
import {requestAudience} from './requestAudience'
import {requestIdentityAuthResetAccess} from './requestIdentityAuthResetAccess'
import {requestIdentityVerifyAccess} from './requestIdentityVerifyAccess'
import {resetIdentityAuth} from './resetIdentityAuth'
import {sessionCreated} from '../event/sessionCreated'
import {sessionStatusUpdated} from '../event/sessionStatusUpdated'
import {setTranscriptStatus} from './setTranscriptStatus'
import {ssoBegun} from '../event/ssoBegun'
import {tagCreated} from '../event/tagCreated'
import {tagDeleted} from '../event/tagDeleted'
import {tagFound} from '../event/tagFound'
import {tagUpdated} from '../event/tagUpdated'
import {tagsFound} from '../event/tagsFound'
import {transcriptContents} from '../event/transcriptContents'
import {transcriptFound} from 'api/event/transcriptFound'
import {transferAudience} from './transferAudience'
import {updateChannel} from './updateChannel'
import {updateDialogue} from './updateDialogue'
import {updateIdentity} from './updateIdentity'
import {updateIdentityAuth} from './updateIdentityAuth'
import {updateMember} from './updateMember'
import {updateMessage} from './updateMessage'
import {updateQueue} from './updateQueue'
import {updateRealm} from './updateRealm'
import {updateSession} from './updateSession'
import {updateTag} from './updateTag'
import {updateUser} from './updateUser'
import {updateUserMessages} from './updateUserMessages'
import {userFound} from '../event/userFound'
import {userUpdated} from '../event/userUpdated'
import {verifyIdentity} from './verifyIdentity'

export const ninchatActionExtraReducers = (builder: ActionReducerMapBuilder<NinchatState>): void => {
    builder
        .addCase(acceptAudience.fulfilled, channelJoined.reducer)
        .addCase(addMember.fulfilled, (state, action) => action.meta.reducer(state, action)) // channelMemberJoined | queueMemberJoined | realmMemberJoined
        .addCase(assignTranscript.fulfilled, ack)
        .addCase(beginSso.fulfilled, ssoBegun)
        .addCase(closeSession.fulfilled, (state) => state)
        .addCase(closeSession.pending, (_, action) => action.meta.reducer()) // pendingCloseSession
        .addCase(createAccess.fulfilled, accessCreated)
        .addCase(createComment.fulfilled, commentCreated)
        .addCase(createIdentity.fulfilled, identityCreated)
        .addCase(createIdentityWithAuthResetAccess.fulfilled, identityCreated)
        .addCase(createMasterKey.fulfilled, masterKeyCreated)
        .addCase(createTag.fulfilled, tagCreated)
        .addCase(createSession.fulfilled, sessionCreated.reducer)
        .addCase(deleteIdentity.fulfilled, identityDeleted)
        .addCase(deleteMasterKey.fulfilled, masterKeyDeleted)
        .addCase(deleteMessage.fulfilled, messageUpdated)
        .addCase(deleteTag.fulfilled, tagDeleted)
        .addCase(describeAccess.fulfilled, accessFound)
        .addCase(describeChannel.fulfilled, channelFound)
        .addCase(describeFile.fulfilled, fileFound)
        .addCase(describeFile.pending, (state, action) => action.meta.reducer(state, action)) // pendingDescribeFile
        .addCase(describeFile.rejected, (state, action) => action.meta.reducer?.(state, action)) // rejectedDescribeFile
        .addCase(describeIdentity.fulfilled, identityFound)
        .addCase(describeQueue.fulfilled, queueFound)
        .addCase(describeQueueTranscripts.fulfilled, queueTranscriptsFound)
        .addCase(describeRealm.fulfilled, realmFound)
        .addCase(describeRealm.pending, (state, action) => action.meta.reducer(state, action)) // pendingDescribeRealm
        .addCase(describeRealm.rejected, (state, action) => action.meta.reducer?.(state, action)) // rejectedDescribeRealm
        .addCase(describeRealmQueues.fulfilled, realmQueuesFound)
        .addCase(describeTag.fulfilled, tagFound)
        .addCase(describeTags.fulfilled, tagsFound)
        .addCase(describeTranscript.fulfilled, transcriptFound)
        .addCase(describeUser.fulfilled, userFound)
        .addCase(discoverJitsi.fulfilled, jitsiDiscovered)
        .addCase(endAudience.fulfilled, dialogueUpdated)
        .addCase(getAudienceRangeComments.fulfilled, audienceRangeCommentContents)
        .addCase(getQueueActivity.fulfilled, queueActivity)
        .addCase(getQueueStats.fulfilled, queueStatsContents)
        .addCase(getTranscript.fulfilled, transcriptContents)
        .addCase(joinChannel.fulfilled, (state, action) => action.meta.reducer(state, action)) // channelJoined | realmJoined
        .addCase(joinPermissionGroup.fulfilled, joinPermissionGroupAck)
        .addCase(loadHistory.fulfilled, (state, action) => action.meta.reducer(state, action)) // historyResults | messageReceived
        .addCase(partChannel.fulfilled, channelParted.reducer)
        .addCase(createQueue.fulfilled, queueCreated)
        .addCase(registerAudience.fulfilled, audienceRegistered)
        .addCase(removeMember.fulfilled, (state, action) => action.meta.reducer(state, action)) // channelMemberParted | queueMemberParted | realmMemberParted
        .addCase(requestAudience.fulfilled, audienceEnqueued)
        .addCase(requestIdentityAuthResetAccess.fulfilled, accessCreated)
        .addCase(requestIdentityVerifyAccess.fulfilled, accessCreated)
        .addCase(resetIdentityAuth.fulfilled, identityUpdated)
        .addCase(sendMessage.fulfilled, (state, action) => action.meta.reducer(state, action)) // messageReceived
        .addCase(sendMessage.pending, (state, action) => action.meta.reducer(state, action)) // pendingSendMessage
        .addCase(sendMessage.rejected, (state, action) => action.meta.reducer?.(state, action)) // rejectedSendMessage
        .addCase(removePendingMessage, (state, action) => {
            const messages = state._messages[action.payload.chatId]?.messages
            if (messages) {
                state._messages[action.payload.chatId].messages = messages.filter((message) => message.message_id !== action.payload.messageId)
            }
        })
        .addCase(setTranscriptStatus.fulfilled, ack)
        .addCase(transferAudience.fulfilled, (state, action) => action.meta.reducer(state, action)) // channelParted | channelUpdated
        .addCase(updateChannel.fulfilled, channelUpdated.reducer)
        .addCase(createChannel.fulfilled, channelCreated)
        .addCase(updateDialogue.fulfilled, dialogueUpdated)
        .addCase(updateIdentity.fulfilled, identityUpdated)
        .addCase(updateIdentityAuth.fulfilled, identityUpdated)
        .addCase(updateMember.fulfilled, (state, action) => action.meta.reducer(state, action)) // channelMemberUpdated | realmMemberUpdated
        .addCase(updateMessage.fulfilled, messageUpdated)
        .addCase(updateQueue.fulfilled, queueUpdated)
        .addCase(updateRealm.fulfilled, realmUpdated)
        .addCase(updateSession.fulfilled, sessionStatusUpdated)
        .addCase(updateTag.fulfilled, tagUpdated)
        .addCase(updateUser.fulfilled, userUpdated.reducer)
        .addCase(updateUserMessages.fulfilled, messageUpdated)
        .addCase(verifyIdentity.fulfilled, identityUpdated)
}
